import React, {useContext, useRef} from "react";
import "./ImpactStrip.scss";
import {impactMetrics} from "../../portfolio";
import StyleContext from "../../contexts/StyleContext";

// Read the current translateX (in px) from a CSS-animated element
function getComputedTX(el) {
  try {
    return new DOMMatrix(window.getComputedStyle(el).transform).m41;
  } catch {
    return 0;
  }
}

export default function ImpactStrip() {
  const {isDark} = useContext(StyleContext);
  const trackRef = useRef(null);
  // drag.current: null when idle, object when pointer is down
  const drag = useRef(null);
  // hovered.current: true while mouse is inside the marquee
  const hovered = useRef(false);

  if (!impactMetrics || impactMetrics.length === 0) return null;

  // Double the list so the seamless -50% loop works
  const track = [...impactMetrics, ...impactMetrics];

  /* ── Resume animation synced to current pixel position ── */
  const resumeAtPosition = (el, tx) => {
    const hw = el.offsetWidth / 2; // half-track = one item-set width
    const progress = hw > 0 ? Math.min(1, Math.max(0, -tx / hw)) : 0;
    const delay = -(progress * 32); // negative delay = skip ahead

    el.style.removeProperty("transform");
    el.style.removeProperty("animationPlayState");
    el.style.animation = "none";
    void el.offsetHeight; // force reflow so the next line resets the animation
    el.style.animation = `marquee-scroll 32s ${delay}s linear infinite`;
    // Keep paused if mouse is still over the strip
    if (hovered.current) el.style.animationPlayState = "paused";
  };

  /* ── Hover (desktop) ── */
  const onMouseEnter = () => {
    hovered.current = true;
    if (!drag.current) {
      const el = trackRef.current;
      if (el) el.style.animationPlayState = "paused";
    }
  };

  const onMouseLeave = () => {
    hovered.current = false;
    if (!drag.current) {
      const el = trackRef.current;
      if (el) el.style.animationPlayState = "running";
    }
  };

  /* ── Drag / swipe ── */
  const onPointerDown = e => {
    const el = trackRef.current;
    if (!el) return;
    const tx = getComputedTX(el);
    drag.current = {
      startX: e.clientX,
      startY: e.clientY,
      startTX: tx,
      currentTX: tx,
      moved: false,
      captured: false,
      cancelled: false
    };
    // Freeze animation immediately so position doesn't drift while we detect intent
    el.style.animationPlayState = "paused";
  };

  const onPointerMove = e => {
    const s = drag.current;
    if (!s || s.cancelled) return;
    const el = trackRef.current;
    if (!el) return;

    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;

    // Direction detection phase
    if (!s.moved) {
      if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      if (Math.abs(dy) > Math.abs(dx)) {
        // Vertical intent → cancel drag, restore animation
        s.cancelled = true;
        el.style.animationPlayState = hovered.current ? "paused" : "running";
        drag.current = null;
        return;
      }
      // Horizontal confirmed → capture pointer
      s.moved = true;
      if (!s.captured) {
        e.currentTarget.setPointerCapture(e.pointerId);
        s.captured = true;
      }
    }

    // Horizontal drag — update transform
    const hw = el.offsetWidth / 2;
    let tx = s.startTX + dx;
    // Wrap to [-hw, 0) for seamless looping
    if (hw > 0) tx = (((tx % hw) + hw) % hw) - hw;
    s.currentTX = tx;
    el.style.transform = `translate3d(${tx}px, 0, 0)`;
  };

  const onPointerUp = () => {
    const s = drag.current;
    drag.current = null;
    const el = trackRef.current;
    if (!el || !s) return;

    if (s.moved) {
      resumeAtPosition(el, s.currentTX);
    } else {
      // Tap without drag — restore state
      el.style.animationPlayState = hovered.current ? "paused" : "running";
    }
  };

  return (
    <section
      className={`impact${isDark ? " dark" : ""}`}
      aria-label="Key impact metrics"
    >
      {/* aria-hidden on the marquee — screen readers get the real content below */}
      <div
        className="impact__marquee"
        aria-hidden="true"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div ref={trackRef} className="impact__track">
          {track.map((m, i) => (
            <div key={i} className="impact__item">
              <span className="impact__value">{m.value}</span>
              <span className="impact__label">{m.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Screen-reader-only accessible list */}
      <ul className="impact__sr-only">
        {impactMetrics.map((m, i) => (
          <li key={i}>
            {m.value} — {m.label}
          </li>
        ))}
      </ul>
    </section>
  );
}
